import React from 'react';
import { Text } from 'slate'
import { Editor } from 'slate-react';

import {
  NODE_TYPE as NT,
  MARK_TYPE as MT,
  TYPEAHEAD_ACTION as TA,
  RENDER_MODE as RM,
} from 'shared/constants/slate';
import { BASE_COLOR } from 'shared/constants/colors';
import {
  debounce,
  isInsideViewport,
  getSelectionBoundingRect,
} from 'shared/helpers';
import { classes as cn } from 'shared/styles';

import { cx, css } from 'helpers';
import { ErrorBoundary } from 'components/utils';
import { Popover, popWithMotion } from 'components/elems';

import {
  HandleImage,
  HandleReceiver,
  DropOrPasteImage,
  SoftBreak,
  EditHeading,
  EditList,
    editListOpts,
  EditCode,
    editCodeOpts,
  EditBlockquote,
    editBlockquoteOpts,
  PrismHighlight,
  onPasteGuessSyntax,
  AutoTypeahead,
    autoTypeaheadOtps,
} from './plugins';

import {
  validateDocument,
  validateHeadline,
  validateParagraph,
  validateReceiver,
  // validateEditCodeMore,
} from './validations';

import { renderDefaultNode, } from './nodes';
import { renderDefaultMark, } from './marks';
import {
  hasActiveMark,
  topLevelBlocksInSel,
  toggleMarkWorkaround,
  onlyCollapsedInTopLevelParagraphAndEmpty,
} from './utils';
import { CaretShadowFrame, } from './decors';
import Placeholder from './Placeholder';
import {
  HOVER_MENU_WIDTH,
  HOVER_MENU_HEIGHT,
  HoverMenuFrame,
  renderFormatBtn,
} from './decors/HoverMenu';
import { CommandMenu } from './decors';


const autoTypeaheadPlugin = AutoTypeahead(autoTypeaheadOtps);
const { Typeahead } = autoTypeaheadPlugin;


/*
 * Code Block & Syntax Highlighting
 */
const highlightCodePlugin = {
  ...PrismHighlight({
    onlyIn: node => node.type === NT.codeBlock,
    getSyntax: node => node.data.get('syntax'),
  }),
  onPaste: onPasteGuessSyntax,
};

const editHeadingPlugin = EditHeading();
const editCodePlugin = EditCode(editCodeOpts);
// editCodePlugin.validateNode = validateEditCodeMore(editCodeOpts);

const editListPlugin = EditList(editListOpts);
const editBlockquotePlugin = EditBlockquote(editBlockquoteOpts);

const editDocument = {validateNode: validateDocument};
const editHeadline = {validateNode: validateHeadline};
const editParagraph = {validateNode: validateParagraph};

const coreEditPlugins = [
  highlightCodePlugin,  // Need to come before editCodePlugin, editLinkPlugin!
  editCodePlugin,
  editListPlugin,
  editBlockquotePlugin,
  editDocument,
  editHeadline,
  editParagraph,
  editHeadingPlugin,  // Keep this last of this group
];


const receiverHandlers = HandleReceiver()
const receiverPlugin = {
  ...receiverHandlers,
  validateNode: validateReceiver
}

const imagePlugin = HandleImage();
const dropOrPasteImagePlugin = DropOrPasteImage();

const extraEditPlugins = [
  receiverPlugin,
  imagePlugin,  // before drag-or-drop plugin
  dropOrPasteImagePlugin,
];


const basePlugins = [
  SoftBreak({ onlyIn: [NT.headline], shift: true }),
  ...coreEditPlugins,
  ...extraEditPlugins,
  autoTypeaheadPlugin,
];



class MediumRareEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hoverMenu: {
        isOpen: false,
        position: null,
        inInputMode: false,
      },

      caretShadow: {
        position: null,
      },

      typeahead: {
        action: null,
        capture: null,
      },
    };

    const { firstPlugins = [], lastPlugins = [] } = props;

    const middlePlugins = props.readOnly ? [] : basePlugins;
    this.plugins = [ ...firstPlugins, ...middlePlugins, ...lastPlugins ];

    // "Last known good" state.
    this.lkgValue = props.value;

    // Maybe redundant, but for perf safety.
    this.doSyncPortalMenuPos = false;

    // PERF: See componentWillReceiveProps.
    this.debouncePortalMenuPosSync = debounce(function() {
      this.doSyncPortalMenuPos = true;
      this.componentDidUpdate();
    }, 20);  // Keep it low but not TOO low.

    this.onTypeahead = this.onTypeahead.bind(this);
    this.offTypeahead = this.offTypeahead.bind(this);
    this.rollbackEditorChange = this.rollbackEditorChange.bind(this);
    this.updateCaretShadowPosition = this.updateCaretShadowPosition.bind(this);
  }

  componentDidMount() {
    // `autoFocus` prop alone is pretty fickle,
    // so maybe this will fix it.. just maybe? haha
    if (this.props.autoFocus && this.editor) {
      this.editor.focus();
    }

    this.updateHoverMenuVisibility();

    // doesn't matter which dom elem is getting scrolled,
    // detect it anyway. i.e. useCapture = true
    window.addEventListener("resize", this.updateHoverMenuPosition, true);
    window.addEventListener("resize", this.updateCaretShadowPosition, true);
  }

  // IMPORTANT:
  // Trying to update hover menu position in componentDidUpdate
  // at every update seems to cause infinite loop in rendering cycle.
  // So, instead we want to use `doSyncPortalMenuPos` flag to
  // indicate we want to redraw HoverMenu
  componentWillReceiveProps(nextProps) {
    const nextValue = nextProps.value;
    const currValue = this.props.value;

    // Keep track of one editor state behind, assuming
    // it was a "good state" rendered correctly, in case
    // we want to rollback to this state.
    this.lkgValue = currValue;

    if (nextValue.isBlurred) {
      return;
    }

    // PERF: We want to avoid recalculating HoverMenu position
    // and redrawing it so often (e.g. selection expanding via keyboard)
    // such that it results in a significant perf hit.
    // Here we debounce to reduce redraws.
    this.debouncePortalMenuPosSync();
  }

  componentDidUpdate() {
    this.updateHoverMenuVisibility();

    if (this.doSyncPortalMenuPos) {
      this.doSyncPortalMenuPos = false;
      this.updateHoverMenuPosition();
      this.updateCaretShadowPosition();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateHoverMenuPosition);
    window.removeEventListener("resize", this.updateCaretShadowPosition);
  }

  rollbackEditorChange = () => {
    // Back to last konwn good state.
    this.props.onChange({value: this.lkgValue});
  }

  updateHoverMenuVisibility = () => {
    const isHoverMenuOpen = this.state.hoverMenu.isOpen;
    const { value } = this.props;

    const topLevelBlocks = topLevelBlocksInSel(value, { withTrim: true });
    const includesExempted = topLevelBlocks.some(tlb =>
      [NT.headline, NT.codeBlock].includes(tlb.type)
    );

    const showHoverMenu = (
      this.state.hoverMenu.inInputMode
      || (
        value.isFocused
        && value.isExpanded
        && !includesExempted
      )
    );

    if (showHoverMenu && !isHoverMenuOpen) {
      return this.setState({
        hoverMenu: {
          isOpen: true,
          position: this.calcHoverMenuPosition(),
          inInputMode: false,
        },
      });
    }

    if (!showHoverMenu && isHoverMenuOpen) {
      return this.setState({
        hoverMenu: {
          isOpen: false,
          position: null,
          inInputMode: false,
        },
      });
    }
  }

  calcHoverMenuPosition = () => {
    if (this.state.hoverMenu.inInputMode) {
      // Last position still valid.
      return this.state.hoverMenu.position;
    }

    const selRect = getSelectionBoundingRect();
    if (!selRect) {
      return this.state.hoverMenu.position;
    }

    if (!this.anchor) {
      return this.state.hoverMenu.position;
    }
    const anchorRect = this.anchor.getBoundingClientRect();

    const top = (
      selRect.top
      - anchorRect.top
      + window.scrollY
      - HOVER_MENU_HEIGHT
    );

    const left = (
      selRect.left
      - anchorRect.left
      + window.scrollX
      - HOVER_MENU_WIDTH/2
      + selRect.width/2
    );

    const position = {
      top: `${top}px`,
      left: `${left}px`,
    };

    return position;
  }

  updateHoverMenuPosition = () => {
    // Either closed or unmounted, noop.
    if (!this.state.hoverMenu.isOpen) {
      return;
    }

    const position = this.calcHoverMenuPosition();

    return this.setState({
      hoverMenu: { ...this.state.hoverMenu, position },
    });
  }

  renderHoverMenu = () => {
    if (this.props.readOnly) {
      return null;
    }

    const { isOpen } = this.state.hoverMenu;
    if (!isOpen) {
      return null;
    }

    // PERF: If we can tell that hoverMenu is positioned outside
    // current viewport, hence not going to be visible anyway to
    // the user, no point in calculating below to render.
    const hoverMenu = this.anchor.firstChild;
    if (hoverMenu && !isInsideViewport(hoverMenu)) {
      return null;
    }

    const {
      availableMarkTypes = [MT.bold, MT.italic, MT.strikethrough, MT.code],
    } = this.props;

    const position = this.state.hoverMenu.position || this.calcHoverMenuPosition() || {};
    const style = {
      width: HOVER_MENU_WIDTH,
      height: HOVER_MENU_HEIGHT,
      ...position
    };

    const topLevelBlocks = topLevelBlocksInSel(
      this.props.value, { withTrim: true }
    );

    const markFormatBtns = availableMarkTypes.map(type =>
      this.renderMarkFormatBtn(type, topLevelBlocks)
    );


    return (
      <HoverMenuFrame
        className="absolute"
        isVisible={isOpen}
        style={style}
      >
        <div className={cx(
          cn.firstLastChildBR2,
          "w-100 flex items-center justify-between"
        )}>
          {this.state.hoverMenu.inInputMode ? (
            null
            // TODO..
            // <HoverMenuLinkInput
            //   inputPlaceholder="Paste or type a link..."
            //   onInputEnter={this.handleURLInputFromHoverMenu}
            //   onInputCancel={this.dismissHoverMenuInputMode}
            // />
          ) : (
            markFormatBtns
          )}
        </div>
      </HoverMenuFrame>
    );
  }

  renderMarkFormatBtn = (type, topLevelBlocks) => {
    const { value } = this.props;

    // Mark(s) should be active if every character
    // has it, i.e. activeMarks.
    const isActive = hasActiveMark(value, type);
    const onMouseDown = e => this.handleClickOnMarkFormatButton(e, type);

    const isDisabled = topLevelBlocks.every(block =>
      block.type === NT.codeBlock
    );

    const status = { isActive, isDisabled };
    const props = { size: 14 };
    return renderFormatBtn(type, onMouseDown, status, props);
  }

  handleClickOnMarkFormatButton = (e, type) => {
    e.preventDefault();

    const { value } = this.props;
    const change = toggleMarkWorkaround(value.change(), type);
    this.props.onChange(change);
  }

  calcCaretShadowPosition = () => {
    if (this.props.readOnly) {
      return null;
    }

    const selRect = getSelectionBoundingRect();
    if (!selRect) {
      return this.state.caretShadow.position;
    }

    if (!this.anchor) {
      return this.state.caretShadow.position;
    }

    const anchorRect = this.anchor.getBoundingClientRect();

    const top = (
      selRect.top
      - anchorRect.top
      + window.scrollY
    );

    const left = (
      selRect.left
      - anchorRect.left
      + window.scrollX
    );

    if (top < 0) {
      // HACK: Not sure why.. but when pressing down arrow key
      // up or down and caret is moving really fast, sometimes
      // selRect.top can be zero causing calculated top to be negative
      // and toolbarMenu ends up at the top of the window..

      // Schedule another try for later..
      this.debouncePortalMenuPosSync();
      return this.state.caretShadow.position;
    }

    const position = { top: `${top}px`, left: `${left}px` };
    return position;
  }

  updateCaretShadowPosition = () => {
    // Typeahead is currently rendering through caretShadow,
    // so lock the position when typeahead is active.
    if (!!this.state.typeahead.action) {
      return;
    }

    const position = this.calcCaretShadowPosition();
    if (!position) {
      return;
    }

    return this.setState({
      caretShadow: { ...this.state.caretShadow, position },
    });
  }

  renderPlaceholderText = (value) => {
    const forParagraph = onlyCollapsedInTopLevelParagraphAndEmpty(value);
    if (forParagraph) {
      return "Press / for commands"
    }

    // const forHeading = onlyInHeadingCollapsedAndEmpty(value);
    // if (forHeading) {
    //   return "Type header here.."
    // }

    return null;
  }

  renderCaretShadow = () => {
    const position = (
         this.state.caretShadow.position
      || this.calcCaretShadowPosition()
      || {}
    );

    const { value } = this.props;
    const placeholder = this.renderPlaceholderText(value);

    const { isCollapsed, startBlock } = value;
    const t = this.state.typeahead;

    const noTrackingDotIn = [
      NT.headline,
      NT.image,
      NT.embed,
    ];

    const hasTrackingDot = (
      isCollapsed
      && !placeholder
      && !t.action
      && (startBlock && !noTrackingDotIn.includes(startBlock.type))
    );

    const Menu = {
      [TA.command]: CommandMenu,
    }[t.action];

    return (
      <CaretShadowFrame
        placeholder={placeholder}
        position={position}
        hasTrackingDot={hasTrackingDot}
      >
        <Popover
          isOpen={!!this.state.typeahead.action}
          placement="top-start"
        >
          {/* Empty target */}
          <div />

          {popWithMotion(
            <Typeahead Menu={Menu} inputValue={t.capture} />
          )}
        </Popover>
      </CaretShadowFrame>
    );
  }

  onTypeahead = ({ action, capture }) => {
    const t = this.state.typeahead;

    // Only proceed to update typeahead state if they aren't
    // the same. Otherwise, it results in infinite loops..
    if (t.action === action && t.capture === capture) {
      return;
    }

    const typeahead = { action, capture };
    this.setState({ typeahead });
  }

  offTypeahead = () => {
    const t = this.state.typeahead;

    // No need to update if typeahead already off.
    if (!t.action) {
      return;
    }

    const typeahead = { action: null, capture: null };
    this.setState({ typeahead });
  }

  renderNode = (props) => {
    const {
      renderNode: _renderNode = () => null,
    } = this.props;

    const node = _renderNode(props);
    if (node) {
      return node;
    }

    props = { ...props, renderMode: RM.RENDER_FOR_EDIT };
    return renderDefaultNode(props);
  }

  renderMark = (props) => {
    const {
      renderMark: _renderMark = () => null,
    } = this.props;

    const mark = _renderMark(props);
    if (mark) {
      return mark;
    }

    return renderDefaultMark(props);
  }

  renderPlaceholder = (props) => {
    const { node } = props;

    if (node.kind !== 'block') return;
    if (!Text.isTextList(node.nodes)) return;

    const {
      placeholder,
      renderPlaceholder: _renderPlaceholder,
    } = this.props;

    if (typeof _renderPlaceholder === 'function') {
      return _renderPlaceholder(props);
    }

    if (placeholder) {
      return <Placeholder>{placeholder}</Placeholder>;
    }
  }

  renderEditor = (value) => {
    const {
      className,
      schema,
      placeholder,
      autoFocus = true,
      readOnly = false,
    } = this.props;

    return (
      <Editor
        ref={ref => this.editor = ref}
        schema={schema}
        value={value}
        autoFocus={autoFocus}
        readOnly={readOnly}
        placeholder={placeholder}
        className={cx(
          '-typography-base',
          'black-80',
          editorClass,
          className,
        )}
        renderNode={this.renderNode}
        renderMark={this.renderMark}
        renderPlaceholder={this.renderPlaceholder}
        onChange={this.props.onChange}
        onKeyDown={this.props.onKeyDown}
        decorateNode={this.props.decorateNode}
        plugins={this.plugins}
        typeahead={{
          ...this.state.typeahead,
          on: this.onTypeahead,
          off: this.offTypeahead,
        }}
        etc={{
          updateCaretShadowPosition: this.updateCaretShadowPosition,
        }}
      />
    );
  }

  renderEditorWithEB = () => {
    const fallback = this.renderEditor(this.lkgValue);
    return (
      <ErrorBoundary
        fallback={fallback}
        onErrored={this.rollbackEditorChange}
      >
        {this.renderEditor(this.props.value)}
      </ErrorBoundary>
    );
  }

  render() {
    return (
      <div className="relative">
        {/*
          1. Must be positioned at top to calculate absolute position.
          2. Keep hover menu as first child (refer to visibility test)
        */}
        <div ref={ref => this.anchor = ref}>
          {this.renderHoverMenu()}
          {this.renderCaretShadow()}
        </div>

        {this.renderEditorWithEB()}
      </div>
    );
  }
}


const editorClass = css({
  caretColor: BASE_COLOR.caretBlue,

  // Vertical spacing between top level blocks.
  '& > *': { margin: '0 auto 1.5rem', },
  '& > h2': { margin: '2.25rem auto 0.75rem' },
});


export default MediumRareEditor;



// WEBPACK FOOTER //
// ./src/components/slate/MediumRareEditor.js